import * as Sentry from "@sentry/react";
import { createContext, useContext, PropsWithChildren, useCallback, useMemo } from "react";

interface TraceContextValue {
    createTraceId: () => string;
    logTraceEvent: (payload: TraceLogPayload) => void;
    getCurrentTraceId: () => string | undefined;
    getCurrentSpanId: () => string | undefined;
}

interface TraceLogPayload {
    traceId?: string;
    event: string;
    level?: "info" | "warn" | "error" | "debug";
    message?: string;
    metadata?: Record<string, unknown>;
}

const TraceContext = createContext<TraceContextValue | null>(null);

export function TraceProvider({ children }: PropsWithChildren) {
    const createTraceId = useCallback(() => {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        const traceId = Array.from(bytes)
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');

        return traceId;
    }, []);

    const getCurrentTraceId = useCallback(() => {
        const activeSpan = Sentry.getActiveSpan();
        if (activeSpan) {
            const spanContext = activeSpan.spanContext();
            return spanContext.traceId;
        }
        return undefined;
    }, []);

    const getCurrentSpanId = useCallback(() => {
        const activeSpan = Sentry.getActiveSpan();
        if (activeSpan) {
            const spanContext = activeSpan.spanContext();
            if (spanContext.spanId) {
                return spanContext.spanId;
        }
        }

        const bytes = new Uint8Array(8);
        crypto.getRandomValues(bytes);
        const spanId = Array.from(bytes)
            .map(b => b.toString(16).padStart(2, '0'))
            .join('')
            .substring(0, 16);

        return spanId;
    }, []);

    const logTraceEvent = useCallback((payload: TraceLogPayload) => {
        const traceId = payload.traceId || getCurrentTraceId() || createTraceId();
        const sessionId = sessionStorage.getItem("sessionId") || "";

        const logData = {
            sessionId,
            traceId,
            ...payload,
            timestamp: new Date().toISOString(),
        };

        Sentry.addBreadcrumb({
            message: payload.message || payload.event,
            level: payload.level === "error" ? "error" : payload.level === "warn" ? "warning" : "info",
            data: {
                ...logData,
                service: "webapp",
            },
        });
    }, [createTraceId, getCurrentTraceId]);

    const value = useMemo(
        () => ({ createTraceId, logTraceEvent, getCurrentTraceId, getCurrentSpanId }),
        [createTraceId, logTraceEvent, getCurrentTraceId, getCurrentSpanId]
    );

    return <TraceContext.Provider value={value}>{children}</TraceContext.Provider>;
}

export const useTrace = () => {
    const ctx = useContext(TraceContext);
    if (!ctx) throw new Error("useTrace must be used inside TraceProvider");
    return ctx;
};
